/**
 * @fileoverview O começo da esteira de cadastro (etapa 5 dos planos), para quem
 * entrou e ainda não tem conta (a sessão do onboarding): o nome da Empresa e o
 * da primeira conta, no layout das telas de entrada. Criada a conta, a sessão
 * vira a de sempre e os outros passos (IA, WhatsApp, equipe) seguem dentro do
 * app, em /configurar. Quem já tem conta e abre este endereço vai para onde a
 * esteira dele está.
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";

import AuthLayout from "../../components/AuthLayout";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import TermsCheckbox from "../../components/AuthLayout/TermsCheckbox";
import { errorText } from "../../components/AuthLayout/authHelpers";
import SetupProgress, { SetupInfo } from "../../components/CompanySetup/SetupProgress";
import useSetupStyles from "../../components/CompanySetup/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const NAME_MAX = 80;
const t = (key, options) => i18n.t(`companySetup.${key}`, options);

const CompanySetup = () => {
  const form = useAuthFormStyles();
  const classes = useSetupStyles();
  const { isAuth, loading } = useContext(AuthContext);
  const [state, setState] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const stage = state?.stage;
  // Quem se cadastrou antes do aceite obrigatório aceita aqui, ao criar a Empresa.
  const needsTerms = (stage === "company" || stage === "account") && state?.termsAccepted === false;
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState("");

  useEffect(() => {
    if (loading || !isAuth) return undefined;
    let alive = true;
    api
      .get("/onboarding/setup")
      .then(({ data }) => {
        if (!alive) return;
        // A conta já existe (criada aqui, em outra aba ou em outro aparelho): a página
        // recarrega, e a renovação da sessão troca o token do onboarding pelo de sempre.
        // Sem recarregar, a trava do app traria a pessoa de volta para cá, sem fim.
        if (data.stage === "steps" || data.stage === "done") {
          window.location.replace(data.stage === "steps" ? "/configurar" : "/");
          return;
        }
        setState(data);
        // A conta começa com o nome da Empresa: quem tem uma loja só não digita de novo.
        setName(data.companyName || "");
      })
      .catch((err) => alive && setError(errorText(err)));
    return () => {
      alive = false;
    };
  }, [loading, isAuth]);

  // Cada passo começa no campo.
  useEffect(() => {
    if (stage && inputRef.current) inputRef.current.focus();
  }, [stage]);

  if (!loading && !isAuth) return <Redirect to="/login" />;

  const submit = async (event) => {
    event.preventDefault();
    const clean = name.replace(/\s+/g, " ").trim();
    if (clean.length < 2) {
      setError(i18n.t(`backendErrors.${stage === "account" ? "ERR_SETUP_INVALID_ACCOUNT_NAME" : "ERR_SETUP_INVALID_COMPANY_NAME"}`));
      return;
    }
    if (needsTerms && !acceptedTerms) {
      setTermsError(i18n.t("backendErrors.ERR_TERMS_NOT_ACCEPTED"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const terms = needsTerms ? { acceptedTerms } : {};
      if (stage === "company") {
        const { data } = await api.post("/onboarding/setup/company", { name: clean, ...terms });
        setState(data);
        setName(data.companyName || clean);
        setBusy(false);
        return;
      }
      // A conta criada troca a sessão do onboarding pela de sempre: o app recarrega nos passos seguintes.
      const { data } = await api.post("/onboarding/setup/account", { name: clean, ...terms });
      localStorage.setItem("token", data.token);
      localStorage.setItem("companyId", String(data.user.companyId));
      localStorage.setItem("userId", String(data.user.id));
      window.location.assign("/configurar");
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const backToCompany = () => {
    setError("");
    setName(state.companyName || "");
    setState({ ...state, stage: "company" });
  };

  // Sair no meio: encerra a sessão do onboarding (a de sempre ainda não existe).
  const leave = async () => {
    try {
      await api.delete("/onboarding/setup/session");
    } catch (err) {
      // Sem resposta, a sessão vence sozinha; a tela sai do mesmo jeito.
    }
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    window.location.assign("/login");
  };

  return (
    <AuthLayout>
      {!state ? (
        <div className={classes.loading}>
          {error ? <div className={`${form.notice} ${form.noticeBad}`}>{error}</div> : <CircularProgress size={24} />}
        </div>
      ) : (
        <div className={form.view} key={stage}>
          <SetupProgress current={stage} compact />
          <h1 className={form.title}>{t(`${stage}.title`)}</h1>
          <p className={form.subtitle}>{t(`${stage}.subtitle`)}</p>
          <form onSubmit={submit} noValidate>
            <div className={form.field}>
              <div className={classes.labelRow}>
                <label className={`${form.label} ${classes.labelInRow}`} htmlFor="setup-name">
                  {t(`${stage}.label`)}
                </label>
                {stage === "account" && <SetupInfo text={t("account.info")} />}
              </div>
              <input
                id="setup-name"
                ref={inputRef}
                className={form.input}
                value={name}
                maxLength={NAME_MAX}
                placeholder={t(`${stage}.placeholder`)}
                autoComplete="organization"
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? "setup-error" : undefined}
                onChange={(event) => setName(event.target.value)}
              />
              {error && (
                <div id="setup-error" className={form.error} role="alert">
                  {error}
                </div>
              )}
            </div>
            {needsTerms && (
              <TermsCheckbox
                id="setup-terms"
                checked={acceptedTerms}
                onChange={(value) => {
                  setAcceptedTerms(value);
                  setTermsError("");
                }}
                error={termsError}
              />
            )}
            <button type="submit" className={form.button} disabled={busy}>
              {busy && <CircularProgress size={16} className={form.spinner} />}
              {t(`${stage}.submit`)}
            </button>
          </form>
          <div className={classes.links}>
            {stage === "account" && (
              <button type="button" className={form.link} onClick={backToCompany} disabled={busy}>
                {t("account.back")}
              </button>
            )}
            <button type="button" className={form.link} onClick={leave} disabled={busy}>
              {t("leave")}
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default CompanySetup;
